import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Dropdown, GlassCard, PageHeader, Segmented, TextInput } from '../../shared/ui'
import { api } from '../../shared/api'
import { notify } from '../../store/notificationStore'
import { useUIStore } from '../../store/uiStore'
import { useThemeStore, THEMES, type ThemeId } from '../../store/themeStore'
import { supportedLngs, lngLabels, type SupportedLng } from '../../shared/i18n/config'
import { useEffectivePermissions } from '../../auth/useEffectivePermissions'
import { useUserPreferences } from '../../auth/useUserPreferences'
import { usePermissions } from '../../auth/usePermissions'
import { modules } from '../registry'

const LANDING_PREF_KEY = 'landingModule'
const LANDING_DEFAULT = ''  // empty = use system default (`/`)

function FirmaEinstellungenKarte() {
  const { t } = useTranslation('shell')
  const [betrag, setBetrag] = useState('')
  const [laedt, setLaedt] = useState(true)
  const [speichert, setSpeichert] = useState(false)
  const [fehler, setFehler] = useState('')

  useEffect(() => {
    let aktiv = true
    api.get<{ geldtueteHoechstbetragCents: number }>('/api/firma/einstellungen')
      .then(data => { if (aktiv) setBetrag(String(data.geldtueteHoechstbetragCents / 100)) })
      .catch((e: Error) => { if (aktiv) setFehler(e.message) })
      .finally(() => { if (aktiv) setLaedt(false) })
    return () => { aktiv = false }
  }, [])

  async function speichern() {
    setSpeichert(true)
    setFehler('')
    try {
      const data = await api.put<{ geldtueteHoechstbetragCents: number }>('/api/firma/einstellungen', {
        geldtueteHoechstbetragCents: Math.round(Number(betrag) * 100),
      })
      setBetrag(String(data.geldtueteHoechstbetragCents / 100))
      notify.success(t('firma.saved'))
    } catch (e) {
      setFehler((e as Error).message)
    } finally {
      setSpeichert(false)
    }
  }

  return (
    <GlassCard radius="xl" className="p-6">
      <h3 className="font-headline font-bold text-base text-on-surface mb-4">{t('firma.title')}</h3>
      <form onSubmit={e => { e.preventDefault(); void speichern() }} className="space-y-3">
        <div className="flex items-end gap-4 flex-wrap">
          <div className="w-full sm:w-[280px]">
            <TextInput
              label={t('firma.limit')} type="number" min={1} max={10000} step={0.01} required
              value={betrag} onChange={e => setBetrag(e.target.value)} disabled={laedt || speichert}
            />
          </div>
          <Button type="submit" icon="save" loading={speichert} disabled={laedt || !betrag}>
            {t('firma.save')}
          </Button>
        </div>
        {fehler && <p role="alert" className="text-sm text-error">{fehler}</p>}
      </form>
    </GlassCard>
  )
}

/**
 * User-settings page.
 *
 * Sections:
 *   • Erscheinungsbild — Theme picker tiles + Dark/Light/System toggle + Privat-Modus hint
 *   • Sprache & Region — language picker
 *   • Privatsphäre — Privat-Modus shortcut info, future auto-blur timers
 *   • Über — version, links
 *
 * The theme picker uses scoped preview tiles (local data-theme on the tile
 * wrapper) so you can see a theme without activating it live. Click = apply.
 */
export default function Settings() {
  const { t, i18n } = useTranslation('shell')
  const navigate = useNavigate()
  const { isAdmin } = usePermissions()

  const { themeId, setTheme } = useThemeStore()
  const { darkMode, toggleDarkMode } = useUIStore()

  // ── Startseite (landing page after login) ──
  const perms = useEffectivePermissions()
  const prefs = useUserPreferences()
  const currentLanding = prefs.get(LANDING_PREF_KEY) ?? LANDING_DEFAULT

  // Build dropdown options: all modules the user can read, plus a "Standard"
  // entry that means "use system default landing (`/`)". Excludes settings
  // itself + nested route-only paths (noRoute) and the access-denied page.
  const landingOptions = useMemo(() => {
    const available = modules
      .filter(m => !m.noRoute)
      .filter(m => m.id !== 'settings')
      .filter(m => perms.canRead(m.id))
      .sort((a, b) => (a.label ?? a.id).localeCompare(b.label ?? b.id, 'de'))
      .map(m => ({ value: m.id, label: m.label ?? m.id }))
    return [
      { value: LANDING_DEFAULT, label: '— Standard (Dashboard) —' },
      ...available,
    ]
  }, [perms])

  async function onChangeLanding(moduleId: string) {
    await prefs.set(LANDING_PREF_KEY, moduleId === LANDING_DEFAULT ? null : moduleId)
  }

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
      <PageHeader
        title="Einstellungen"
        subtitle="Erscheinungsbild, Sprache und Privatsphäre."
        actions={
          <Button
            variant="secondary"
            icon="arrow_back"
            onClick={() => navigate(-1)}
          >
            Zurück
          </Button>
        }
      />

      {/* ── Erscheinungsbild ─────────────────────────────────────── */}
      <GlassCard radius="xl" className="p-6">
        <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
          <div>
            <h3 className="font-headline font-bold text-base text-on-surface">Erscheinungsbild</h3>
            <p className="text-xs text-on-surface-variant mt-0.5">Thema, Dark-Modus und Privat-Modus für die ganze App.</p>
          </div>
        </div>

        {/* Theme picker tiles */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
          {THEMES.map(th => {
            const active = themeId === th.id
            return (
              <button key={th.id} onClick={() => setTheme(th.id as ThemeId)}
                className={`group text-left rounded-2xl p-4 transition-all border-2 ${
                  active
                    ? 'border-primary shadow-lg'
                    : 'border-transparent hover:border-outline-variant/60'
                }`}
              >
                {/* Scoped preview — the tile itself carries data-theme so it
                    shows the theme's surface + primary WITHOUT touching the
                    live app. */}
                <div data-theme={th.id}
                  className="rounded-xl bg-surface-container-lowest p-4 shadow-inner">
                  <div className="flex items-center gap-3">
                    <span className="inline-block w-10 h-10 rounded-lg shadow-sm"
                      style={{ backgroundColor: th.preview.primary }} />
                    <span className="inline-block w-10 h-10 rounded-lg shadow-sm"
                      style={{ backgroundColor: th.preview.surface, border: '1px solid rgba(0,0,0,0.08)' }} />
                    <span className="inline-block w-10 h-10 rounded-lg shadow-sm"
                      style={{ backgroundColor: th.preview.accent }} />
                    <div className="flex-1 flex items-center justify-end">
                      <button type="button" tabIndex={-1}
                        className="bg-primary text-on-primary text-xs font-semibold px-3 py-1.5 rounded-lg pointer-events-none"
                      >
                        Beispiel
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-1.5">
                    <div className="h-1.5 rounded-full bg-primary/60" />
                    <div className="h-1.5 rounded-full bg-primary/30" />
                    <div className="h-1.5 rounded-full bg-primary/10" />
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-headline font-bold text-sm text-on-surface truncate">{th.label}</div>
                    <div className="text-[11px] text-on-surface-variant truncate">{th.tagline}</div>
                  </div>
                  {active && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-primary uppercase tracking-wider">
                      <span className="material-symbols-outlined !text-[14px]">check_circle</span>
                      Aktiv
                    </span>
                  )}
                </div>
              </button>
            )
          })}
        </div>

        {/* Dark / Light toggle */}
        <div className="flex items-center justify-between py-3 border-t border-outline-variant/40">
          <div>
            <div className="text-sm font-semibold text-on-surface">Dark Mode</div>
            <div className="text-xs text-on-surface-variant">Umschalten zwischen Hell und Dunkel. Funktioniert für jedes Thema.</div>
          </div>
          <Segmented<'light' | 'dark'>
            value={darkMode ? 'dark' : 'light'}
            onChange={(v) => { if ((v === 'dark') !== darkMode) toggleDarkMode() }}
            options={[
              { value: 'light', icon: 'light_mode', label: 'Hell' },
              { value: 'dark',  icon: 'dark_mode',  label: 'Dunkel' },
            ]}
          />
        </div>

      </GlassCard>

      {/* ── Sprache & Region ─────────────────────────────────────── */}
      <GlassCard radius="xl" className="p-6">
        <h3 className="font-headline font-bold text-base text-on-surface mb-4">Sprache & Region</h3>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-on-surface">Sprache</div>
            <div className="text-xs text-on-surface-variant">Datumsformate richten sich nach der gewählten Sprache.</div>
          </div>
          <Segmented<SupportedLng>
            value={i18n.resolvedLanguage as SupportedLng}
            onChange={(v) => i18n.changeLanguage(v)}
            options={supportedLngs.map(lng => ({ value: lng, label: lngLabels[lng] }))}
          />
        </div>
      </GlassCard>

      {/* ── Startseite ───────────────────────────────────────────── */}
      <GlassCard radius="xl" className="p-6">
        <h3 className="font-headline font-bold text-base text-on-surface mb-4">Startseite</h3>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-on-surface">Seite nach dem Login</div>
            <div className="text-xs text-on-surface-variant max-w-md mt-0.5">
              Wähle die Seite, die nach jeder Anmeldung automatisch geöffnet wird.
              Du kannst nur Seiten auswählen, für die du Zugriff hast — wenn deine
              Rechte sich ändern, wird automatisch ein erlaubter Bereich geöffnet.
            </div>
          </div>
          <div className="w-full sm:w-[280px]">
            <Dropdown
              value={currentLanding}
              onChange={onChangeLanding}
              options={landingOptions}
              disabled={perms.isLoading || prefs.saving}
              menuZIndex={2000}
            />
          </div>
        </div>
      </GlassCard>

      {isAdmin && <FirmaEinstellungenKarte />}

      {/* ── Über ─────────────────────────────────────────────────── */}
      <GlassCard radius="xl" className="p-6">
        <h3 className="font-headline font-bold text-base text-on-surface mb-4">Über</h3>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-on-surface-variant">Produkt</dt>
          <dd className="text-on-surface font-medium">MC ERP Intranet</dd>
          <dt className="text-on-surface-variant">Build</dt>
          <dd className="text-on-surface font-mono text-xs">{import.meta.env.MODE}</dd>
          <dt className="text-on-surface-variant">Angemeldet als</dt>
          <dd className="text-on-surface text-xs font-mono truncate">{t('auth.login')}</dd>
        </dl>
      </GlassCard>
    </div>
  )
}
